import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useStore } from 'zustand';

import { useReferenceAssistantGateway } from './ReferenceAssistantGatewayProvider';
import { publishProvidedStatementReferences } from '../api/statementReferences';
import type {
  AttachedStatementReference,
  StatementCitationStatus,
  StatementReferenceHelpRequest,
} from '../model/extractedStatements';
import type { PublishProvidedStatementReferencesResult } from '../model/providedReferences';
import {
  getProvideReferenceDraftStore,
  selectFilledCount,
  selectHasPublishableContent,
} from '../state/provideReferenceDraftStore';

import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { useUserScope } from '@/hooks/useUserScope';
import {
  trackProvideReferencesPublished,
  trackProvideReferencesPublishFailed,
} from '@/util/analyticsEvents';

interface UseProvideReferencePublishInput {
  conversationId: string;
  messageSid: string;
  /** Statement indices the expert selected — the rows to read drafts
   * for and publish. */
  statementIndices: readonly number[];
  /** Freshest `attachedStatementReferences` on the message — the
   * read-modify-write base for the verbatim URL append. */
  currentReferences: readonly AttachedStatementReference[];
  /** Freshest `statementReferenceHelpRequests` on the message. */
  currentHelpRequests?: readonly StatementReferenceHelpRequest[];
  /** Freshest `statementCitationStatuses` on the message — base for the
   * `needsCitation: true` mark folded into the publish write. */
  currentStatementCitationStatuses?: readonly StatementCitationStatus[];
  /** Called after a successful (or no-op) publish so the surface can
   * dismiss the wizard. */
  /** Fires after a publish, including a no-op one; the caller confirms
   * it to the expert, since a hook draws nothing. */
  onPublished: (result: PublishProvidedStatementReferencesResult) => void;
}

const GENERIC_PUBLISH_FAILURE_MESSAGE = 'Unable to save references. Please try again.';

/**
 * Publish handler for the citation wizard's provide phase. Reads the
 * per-message provide drafts, builds one entry per selected statement,
 * and writes verbatim URLs + describe-it help requests in a single
 * statement-references write via `publishProvidedStatementReferences`,
 * shown in the bubble before the server answers.
 *
 * Publish is gated on `canPublish`: at least one row has non-empty
 * content. Each row's value is routed by `publishProvidedStatementReferences`
 * — a URL attaches as a reference, plain text becomes a help request.
 * On success the draft store is reset and `onPublished` fires with the
 * result, for the caller's confirmation toast; on
 * failure an inline `errorMessage` is surfaced and the drafts are
 * preserved so the expert can retry.
 *
 * Shared by the native route and the web modal so the publish behavior
 * stays identical across platforms.
 */
export function useProvideReferencePublish({
  conversationId,
  messageSid,
  statementIndices,
  currentReferences,
  currentHelpRequests,
  currentStatementCitationStatuses,
  onPublished,
}: UseProvideReferencePublishInput) {
  const store = getProvideReferenceDraftStore(messageSid);

  const hasPublishableContent = useStore(store, selectHasPublishableContent);

  const filledCount = useStore(store, selectFilledCount);

  const [isPublishing, setIsPublishing] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Synchronous in-flight guard. `isPublishing` only blocks re-entry
  // after a re-render, so a rapid multi-tap fires several presses against
  // the same `canPublish === true` closure before React updates state —
  // each would call the publish write and emit a duplicate
  // `PROVIDE_REFERENCES_PUBLISHED` event. This ref flips synchronously on
  // the first press so the others bail immediately.
  const isPublishingRef = useRef(false);

  const { track } = useChatAnalytics();

  const gateway = useReferenceAssistantGateway();

  const queryClient = useQueryClient();

  const { userId } = useUserScope();

  const canPublish = hasPublishableContent && !isPublishing;

  const handlePublish = useCallback(async () => {
    if (!canPublish || isPublishingRef.current) {
      return;
    }

    isPublishingRef.current = true;

    const { values } = store.getState();

    const entries = statementIndices.map((statementIndex) => ({
      statementIndex,
      value: values.get(statementIndex) ?? '',
    }));

    setIsPublishing(true);

    setErrorMessage(null);

    try {
      const result = await publishProvidedStatementReferences(
        {
          conversationId,
          messageSid,
          entries,
          currentReferences,
          currentHelpRequests,
          currentStatementCitationStatuses,
        },
        gateway,
        { queryClient, userId }
      );

      // A no-op result means every row deduped away (e.g. all URLs were
      // already attached) — nothing was written, so firing PUBLISHED
      // would pollute the funnel with an empty `url_count: 0` event. Skip
      // the event but still close the wizard and clear drafts.
      if (!result.noop) {
        trackProvideReferencesPublished(track, {
          conversationId,
          messageSid,
          statementCount: statementIndices.length,
          urlCount: result.appendedReferences.length,
          helpRequestCount: result.helpRequestedStatementIndices.length,
        });
      }

      store.getState().reset();

      onPublished(result);
    } catch (error) {
      // The publish path routes plain text to a help request rather than
      // rejecting it, so the only reachable failure is the write — always
      // `persist_failed`.
      trackProvideReferencesPublishFailed(track, {
        conversationId,
        messageSid,
        errorKind: 'persist_failed',
      });

      setErrorMessage(error instanceof Error ? error.message : GENERIC_PUBLISH_FAILURE_MESSAGE);
    } finally {
      isPublishingRef.current = false;

      setIsPublishing(false);
    }
  }, [
    canPublish,
    store,
    statementIndices,
    conversationId,
    messageSid,
    currentReferences,
    currentHelpRequests,
    currentStatementCitationStatuses,
    track,
    gateway,
    queryClient,
    userId,
    onPublished,
  ]);

  return { canPublish, filledCount, isPublishing, errorMessage, handlePublish };
}
